import * as THREE from "three";

const ROTATE_90 = Math.PI / 2;

const AXIS_VECTOR = {
    x: new THREE.Vector3(1, 0, 0),
    y: new THREE.Vector3(0, 1, 0),
    z: new THREE.Vector3(0, 0, 1),
};

/** Локальные оси детали в мировой СК (ортонормированный базис). */
export const IDENTITY_AXES = Object.freeze({
    x: Object.freeze([1, 0, 0]),
    y: Object.freeze([0, 1, 0]),
    z: Object.freeze([0, 0, 1]),
});

const snapComponent = (value) => {
    if (Math.abs(value) < 1e-8) return 0;
    if (Math.abs(Math.abs(value) - 1) < 1e-8) return Math.sign(value);
    return Number(value.toFixed(6));
};

const vectorToTuple = (vector) => [
    snapComponent(vector.x),
    snapComponent(vector.y),
    snapComponent(vector.z),
];

export const cloneAxes = (axes = IDENTITY_AXES) => ({
    x: [...(axes.x || IDENTITY_AXES.x)],
    y: [...(axes.y || IDENTITY_AXES.y)],
    z: [...(axes.z || IDENTITY_AXES.z)],
});

/** det(basis): >0 правый, <0 левый (отражение). */
export const axesDeterminant = (axes = IDENTITY_AXES) => {
    const ax = new THREE.Vector3(...(axes?.x || IDENTITY_AXES.x));
    const ay = new THREE.Vector3(...(axes?.y || IDENTITY_AXES.y));
    const az = new THREE.Vector3(...(axes?.z || IDENTITY_AXES.z));
    return ax.dot(new THREE.Vector3().crossVectors(ay, az));
};

/**
 * Правый базис + faceFlip.
 * faceFlip=false: локальный +Y панели = нормаль TOP (как вид в 2D).
 * faceFlip=true: пласть перевёрнута (изнанка / зеркало толщины).
 * Левый базис с бэка/зеркала → впитываем в faceFlip, оси делаем правыми.
 */
export const normalizeAxesWithFaceFlip = (axes = IDENTITY_AXES, faceFlip = false) => {
    const rawX = new THREE.Vector3(...(axes?.x || IDENTITY_AXES.x));
    const rawY = new THREE.Vector3(...(axes?.y || IDENTITY_AXES.y));
    const rawZ = new THREE.Vector3(...(axes?.z || IDENTITY_AXES.z));
    const ax = rawX.lengthSq() > 1e-12
        ? rawX.clone().normalize()
        : new THREE.Vector3(...IDENTITY_AXES.x);
    const ay = rawY.clone().sub(
        ax.clone().multiplyScalar(rawY.dot(ax)),
    );

    // Gram–Schmidt: серверные оси могут содержать накопленную погрешность.
    if (ay.lengthSq() <= 1e-12) {
        const zOrthogonal = rawZ.clone().sub(
            ax.clone().multiplyScalar(rawZ.dot(ax)),
        );
        if (zOrthogonal.lengthSq() > 1e-12) {
            ay.crossVectors(zOrthogonal.normalize(), ax);
        } else {
            const fallback = Math.abs(ax.y) < 0.9
                ? new THREE.Vector3(0, 1, 0)
                : new THREE.Vector3(0, 0, 1);
            ay.copy(fallback.sub(ax.clone().multiplyScalar(fallback.dot(ax))));
        }
    }
    ay.normalize();
    const az = new THREE.Vector3().crossVectors(ax, ay).normalize();

    let flip = Boolean(faceFlip);
    const det = rawX.dot(new THREE.Vector3().crossVectors(rawY, rawZ));
    if (det < -1e-6) {
        ay.negate();
        az.negate();
        flip = !flip;
    }
    return {
        axes: {
            x: vectorToTuple(ax),
            y: vectorToTuple(ay),
            z: vectorToTuple(az),
        },
        faceFlip: flip,
    };
};

/**
 * Базис для API/записи: при faceFlip инвертируем локальную +Y (толщина/TOP),
 * чтобы в DTO ушла фактическая ориентация пласти.
 */
export const axesForPersist = (axes = IDENTITY_AXES, faceFlip = false) => {
    const current = cloneAxes(axes);
    if (!faceFlip) return current;
    return {
        x: current.x,
        y: [-current.y[0], -current.y[1], -current.y[2]],
        z: current.z,
    };
};

/**
 * Линейное преобразование локальных точек инстанса — как orient-группа (applyAxesToObject).
 */
export const buildInstanceOrientationMatrix = (axes = IDENTITY_AXES, faceFlip = false) => {
    const normalized = normalizeAxesWithFaceFlip(axes, faceFlip);
    const visual = axesForPersist(normalized.axes, normalized.faceFlip);
    const ax = new THREE.Vector3(...(visual?.x || IDENTITY_AXES.x)).normalize();
    const ay = new THREE.Vector3(...(visual?.y || IDENTITY_AXES.y)).normalize();
    const az = new THREE.Vector3(...(visual?.z || IDENTITY_AXES.z)).normalize();

    const basisMatrix = new THREE.Matrix4().makeBasis(ax, ay, az);
    const quaternion = new THREE.Quaternion();
    const scale = new THREE.Vector3();
    basisMatrix.decompose(new THREE.Vector3(), quaternion, scale);

    const linear = new THREE.Matrix4();
    linear.compose(
        new THREE.Vector3(0, 0, 0),
        quaternion,
        new THREE.Vector3(
            Math.sign(scale.x) || 1,
            Math.sign(scale.y) || 1,
            Math.sign(scale.z) || 1,
        ),
    );
    return linear;
};

/**
 * Применить базис к Object3D (позиция не трогается).
 * Фактические направляющие (axes + faceFlip) → matrix.decompose:
 * отражение даёт отрицательный scale на нужной оси (X/Y/Z),
 * поэтому axesHelper рисует стрелку и «влево», а не только в +Y.
 */
export const applyAxesToObject = (object, axes, faceFlip = false) => {
    if (!object) return;
    const position = object.position.clone();
    const linear = buildInstanceOrientationMatrix(axes, faceFlip);
    const quaternion = new THREE.Quaternion();
    const scale = new THREE.Vector3();
    linear.decompose(new THREE.Vector3(), quaternion, scale);

    object.position.copy(position);
    object.quaternion.copy(quaternion);
    object.scale.copy(scale);
};

/** Считать базис и faceFlip из Object3D после transform. */
export const poseFromObject = (object, previousFaceFlip = false) => {
    const x = new THREE.Vector3();
    const y = new THREE.Vector3();
    const z = new THREE.Vector3();
    object.updateMatrix();
    object.matrix.extractBasis(x, y, z);
    return normalizeAxesWithFaceFlip(
        {
            x: vectorToTuple(x),
            y: vectorToTuple(y),
            z: vectorToTuple(z),
        },
        previousFaceFlip,
    );
};

/** Считать базис из Object3D после transform (без faceFlip). */
export const axesFromObject = (object) => {
    const {axes} = poseFromObject(object, false);
    return axes;
};

/** ±90° вокруг мировой оси куба: крутим все три направляющих. */
export const rotateAxesBy90 = (axes, axis, sign) => {
    return rotateAxesByAngle(axes, axis, sign * 90);
};

/** Поворот вокруг мировой оси куба на произвольный угол (градусы). */
export const rotateAxesByAngle = (axes, axis, degrees) => {
    const vector = AXIS_VECTOR[axis];
    if (!vector) return cloneAxes(axes);
    const radians = THREE.MathUtils.degToRad(Number(degrees) || 0);
    if (!Number.isFinite(radians) || Math.abs(radians) < 1e-12) {
        return cloneAxes(axes);
    }
    const delta = new THREE.Quaternion().setFromAxisAngle(vector, radians);
    const rotate = (tuple) =>
        vectorToTuple(new THREE.Vector3(...tuple).applyQuaternion(delta).normalize());

    const current = cloneAxes(axes);
    return {
        x: rotate(current.x),
        y: rotate(current.y),
        z: rotate(current.z),
    };
};

const isNearZeroOrOne = (value) => {
    const n = Number(value) || 0;
    return Math.abs(n) < 1e-4 || Math.abs(Math.abs(n) - 1) < 1e-4;
};

/** Базис с компонентами только 0 и ±1 (повороты кратные 90°). */
export const isCardinalAxes = (axes = IDENTITY_AXES) => {
    for (const key of ["x", "y", "z"]) {
        const tuple = axes?.[key] || IDENTITY_AXES[key];
        for (let i = 0; i < 3; i += 1) {
            if (!isNearZeroOrOne(tuple[i])) {
                return false;
            }
        }
    }
    return true;
};

const MIRROR_SIGN = {
    x: [-1, 1, 1],
    y: [1, -1, 1],
    z: [1, 1, -1],
};

/**
 * Зеркало относительно плоскости, перпендикулярной мировой оси куба
 * (плоскость проходит через начало детали — угол с осями).
 */
export const mirrorAxes = (axes, axis) => {
    const sign = MIRROR_SIGN[axis];
    if (!sign) return cloneAxes(axes);
    const reflect = (tuple) => [
        (tuple[0] || 0) * sign[0],
        (tuple[1] || 0) * sign[1],
        (tuple[2] || 0) * sign[2],
    ];
    const current = cloneAxes(axes);
    return {
        x: vectorToTuple(new THREE.Vector3(...reflect(current.x))),
        y: vectorToTuple(new THREE.Vector3(...reflect(current.y))),
        z: vectorToTuple(new THREE.Vector3(...reflect(current.z))),
    };
};

/**
 * Зеркало направляющих: отражаем фактическую ориентацию (с учётом faceFlip)
 * и сохраняем результат как есть (в т.ч. левую СК).
 * Не «впитываем» отражение обратно в faceFlip — иначе оси визуально не двигаются.
 */
export const mirrorAxesWithFaceFlip = (axes, axis, faceFlip = false) => {
    const visual = axesForPersist(axes, faceFlip);
    return {
        axes: mirrorAxes(visual, axis),
        faceFlip: false,
    };
};

const normalizeDeg = (deg) => {
    let value = Number(deg);
    if (!Number.isFinite(value)) return 0;
    value = ((value % 360) + 360) % 360;
    if (value > 180) value -= 360;
    return Math.round(value * 1000) / 1000;
};

/** Округление угла до кратного 90° (−180…180). */
export const snapDegTo90 = (deg) => normalizeDeg(Math.round(normalizeDeg(deg) / 90) * 90);

export const snapEulerTo90 = (euler = {}) => ({
    x: snapDegTo90(euler.x),
    y: snapDegTo90(euler.y),
    z: snapDegTo90(euler.z),
});

/**
 * Произвольный базис → ближайший кардинальный (только 90°).
 * Свободный поворот не поддерживается.
 */
export const quantizeAxesTo90Degrees = (axes = IDENTITY_AXES, faceFlip = false) => {
    let normalized = normalizeAxesWithFaceFlip(axes, faceFlip);
    if (!isCardinalAxes(normalized.axes)) {
        const snappedEuler = snapEulerTo90(axesToEulerDegrees(normalized.axes));
        const fromEuler = eulerDegreesToAxes(snappedEuler);
        normalized = normalizeAxesWithFaceFlip(fromEuler, normalized.faceFlip);
    }
    const nextAxes = cloneAxes(normalized.axes);
    return {
        axes: nextAxes,
        faceFlip: normalized.faceFlip,
        referenceAxes: cloneAxes(nextAxes),
        localRotationAngles: Object.freeze({x: 0, y: 0, z: 0}),
    };
};

/** Эйлер для состояния сцены: кардинальный базис → к 90°, иначе без снэпа. */
export const eulerForInstancePersist = (axes, eulerOverride) => {
    const raw = eulerOverride ?? axesToEulerDegrees(axes);
    if (isCardinalAxes(axes)) {
        return snapEulerTo90(raw);
    }
    return {
        x: normalizeDeg(raw.x),
        y: normalizeDeg(raw.y),
        z: normalizeDeg(raw.z),
    };
};

export {normalizeDeg};

/** Базис → углы Эйлера XYZ в градусах (поворот направляющих в мире). */
export const axesToEulerDegrees = (axes = IDENTITY_AXES) => {
    const matrix = new THREE.Matrix4().makeBasis(
        new THREE.Vector3(...(axes?.x || IDENTITY_AXES.x)),
        new THREE.Vector3(...(axes?.y || IDENTITY_AXES.y)),
        new THREE.Vector3(...(axes?.z || IDENTITY_AXES.z)),
    );
    const euler = new THREE.Euler().setFromRotationMatrix(matrix, "XYZ");
    return {
        x: normalizeDeg(THREE.MathUtils.radToDeg(euler.x)),
        y: normalizeDeg(THREE.MathUtils.radToDeg(euler.y)),
        z: normalizeDeg(THREE.MathUtils.radToDeg(euler.z)),
    };
};

/** Углы Эйлера XYZ (градусы) → базис направляющих. */
export const eulerDegreesToAxes = ({x = 0, y = 0, z = 0} = {}) => {
    const euler = new THREE.Euler(
        THREE.MathUtils.degToRad(Number(x) || 0),
        THREE.MathUtils.degToRad(Number(y) || 0),
        THREE.MathUtils.degToRad(Number(z) || 0),
        "XYZ",
    );
    const matrix = new THREE.Matrix4().makeRotationFromEuler(euler);
    const ax = new THREE.Vector3();
    const ay = new THREE.Vector3();
    const az = new THREE.Vector3();
    matrix.extractBasis(ax, ay, az);
    return {
        x: vectorToTuple(ax),
        y: vectorToTuple(ay),
        z: vectorToTuple(az),
    };
};
